import type { Dayjs } from 'dayjs';
import type { Tagged } from 'type-fest';
import { dayjs } from '../../../shared/utils/date';
import { Account } from '../account';
import { MutableEntity } from '../common';
import { UniqueId } from '../UniqueId';

const codeGenerator = UniqueId.GetFactory(32);

export type SignUpCode = MutableEntity & {
  code: SignUpCode.Code;
  accountId: Account.Id;
  expiresAt: Dayjs;
  consumedAt: Dayjs | undefined;
  revokedAt: Dayjs | undefined;
};

export namespace SignUpCode {
  export const DefaultExpiresInDays = 14;

  export type Code = Tagged<UniqueId, 'signUpCode'>;

  export namespace Code {
    export const Generate = () => codeGenerator.generate() as Code;
    export const FromString = (code: string): Code => codeGenerator.FromString(code) as Code;
  }

  export type Props = MutableEntity.Props & {
    code: string;
    accountId: string;
    expiresAt: string | Date | Dayjs;
    consumedAt?: string | Date | Dayjs;
    revokedAt?: string | Date | Dayjs;
  };

  export const Construct = (props: Props): SignUpCode => {
    return {
      ...MutableEntity.Construct(props),
      code: Code.FromString(props.code),
      accountId: Account.Id.FromString(props.accountId),
      expiresAt: dayjs(props.expiresAt),
      consumedAt: props.consumedAt ? dayjs(props.consumedAt) : undefined,
      revokedAt: props.revokedAt ? dayjs(props.revokedAt) : undefined,
    };
  };

  export namespace IsPending {
    export type Params = {
      signUpCode: SignUpCode;
      now?: Dayjs;
    };
  }
  export const IsPending = (params: IsPending.Params): boolean => {
    const { signUpCode, now = dayjs() } = params;
    return !signUpCode.consumedAt && !signUpCode.revokedAt && signUpCode.expiresAt.isAfter(now);
  };

  export const ValidationStatus = {
    Pending: 'pending',
    Revoked: 'revoked',
    Expired: 'expired',
    Consumed: 'consumed',
    NotFound: 'not_found',
    EmailMismatch: 'email_mismatch',
  } as const;
  export type ValidationStatus = (typeof ValidationStatus)[keyof typeof ValidationStatus];

  export namespace ResolveValidationStatus {
    export type Params = {
      signUpCode: SignUpCode | undefined;
      now?: Dayjs;
    };
  }
  export const ResolveValidationStatus = (params: ResolveValidationStatus.Params): ValidationStatus => {
    const { signUpCode, now = dayjs() } = params;
    if (!signUpCode) return ValidationStatus.NotFound;
    if (signUpCode.consumedAt) return ValidationStatus.Consumed;
    if (signUpCode.revokedAt) return ValidationStatus.Revoked;
    if (!signUpCode.expiresAt.isAfter(now)) return ValidationStatus.Expired;
    return ValidationStatus.Pending;
  };

  export namespace ParseCode {
    export type Params = {
      code: string;
    };
  }
  export const ParseCode = (params: ParseCode.Params): Code | undefined => {
    try {
      return Code.FromString(params.code);
    } catch {
      return undefined;
    }
  };

  export namespace Create {
    export type Params = {
      operatorId: Account.Id;
      accountId: Account.Id;
      expiresAt?: Dayjs;
    };
  }
  export const Create = (params: Create.Params): SignUpCode => {
    const { operatorId, accountId, expiresAt = dayjs().add(DefaultExpiresInDays, 'day') } = params;
    return {
      ...MutableEntity.Create({ operatorId }),
      code: Code.Generate(),
      accountId,
      expiresAt,
      consumedAt: undefined,
      revokedAt: undefined,
    };
  };

  export namespace Consume {
    export type Params = {
      operatorId: Account.Id;
      signUpCode: SignUpCode;
      consumedAt?: Dayjs;
    };
  }
  export const Consume = (params: Consume.Params): SignUpCode => {
    const { operatorId, signUpCode, consumedAt = dayjs() } = params;
    return {
      ...signUpCode,
      ...MutableEntity.Update({ cur: signUpCode, operatorId }),
      consumedAt,
    };
  };

  export namespace Revoke {
    export type Params = {
      operatorId: Account.Id;
      signUpCode: SignUpCode;
      revokedAt?: Dayjs;
    };
  }
  export const Revoke = (params: Revoke.Params): SignUpCode => {
    const { operatorId, signUpCode, revokedAt = dayjs() } = params;
    return {
      ...signUpCode,
      ...MutableEntity.Update({ cur: signUpCode, operatorId }),
      revokedAt,
    };
  };
}
